import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderOpen, faUpload } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { Button } from "@/components/ui/button/Button";
import { CASE_DOCUMENT_ACCEPT } from "@/components/case-checking/caseDocumentAccept";

export type CaseDocumentIngestProps = {
  onUploadFiles: (files: FileList | File[]) => void;
  onBrowseCrm?: () => void;
  /** Import selected Progeny CRM document ids via background ingest + mission triage. */
  onCrmDocumentsAdded?: (progenyDocumentIds: string[], labels?: Record<string, string>) => void;
  /** Disables ingest buttons during blocking upload / pipeline waits. */
  isProcessing: boolean;
  /** Shows ingest progress UI while a runner task is active (including CRM background ingest). */
  showIngestProgress?: boolean;
  stepLabel: string;
  progressPercent?: number | null;
  processedCount?: number | null;
  totalCount?: number | null;
  ingestError: string | null;
  canBrowseCrm: boolean;
  isAgenticProcessing?: boolean;
};

export function CaseDocumentIngestButtons({
  documentIngest,
  compact = false,
  variant = "default",
  testIdPrefix = "case-document-ingest",
  multiple = true,
}: {
  documentIngest: CaseDocumentIngestProps;
  compact?: boolean;
  variant?: "default" | "toolbar";
  testIdPrefix?: string;
  multiple?: boolean;
}) {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const browseCrm = documentIngest.onBrowseCrm;
  const isToolbar = variant === "toolbar";
  const buttonClass = isToolbar
    ? "!h-9 px-2.5 text-xs"
    : compact
      ? "!h-8 text-sm"
      : undefined;
  const uploadLabel = isToolbar ? "Add files" : "Add to queue";
  const crmLabel = isToolbar ? "CRM" : "Add from CRM";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={uploadInputRef}
        type="file"
        multiple={multiple}
        accept={CASE_DOCUMENT_ACCEPT}
        className="hidden"
        data-testid={`${testIdPrefix}-upload-input`}
        onChange={(e) => {
          if (e.target.files?.length) {
            documentIngest.onUploadFiles(e.target.files);
            e.target.value = "";
          }
        }}
      />
      <Button
        color="slate"
        type="button"
        className={buttonClass}
        disabled={documentIngest.isProcessing || documentIngest.isAgenticProcessing}
        loading={documentIngest.isProcessing}
        icon={<FontAwesomeIcon icon={faUpload} className="h-3.5 w-3.5" />}
        onClick={() => uploadInputRef.current?.click()}
        data-testid={`${testIdPrefix}-upload`}
      >
        {uploadLabel}
      </Button>
      {browseCrm ? (
        <Button
          color="slate"
          type="button"
          className={buttonClass}
          disabled={documentIngest.isProcessing || documentIngest.isAgenticProcessing}
          icon={<FontAwesomeIcon icon={faFolderOpen} className="h-3.5 w-3.5" />}
          onClick={() => browseCrm()}
          data-testid={`${testIdPrefix}-browse-crm`}
        >
          {crmLabel}
        </Button>
      ) : null}
    </div>
  );
}
